import type { PipeDetectPhase, Vec3 } from '@/lib/pipe/types';
import { atom } from 'jotai';

/**
 * Pipe detection tool / panel state. Two stages, like the stockpile flow:
 *   1. **Seeking** — the tool is enabled and waiting for the user to click a
 *      point on a pipe surface (`<PipeTool />`).
 *   2. **Review** — after a click, `detectPipe` runs and the result lands in
 *      `pipeDraftAtom`; `<PipeDetectPanel />` opens to show metrics, allow
 *      fine-tuning (radius + endpoint drag), and save.
 *
 * `sourceLayerId` locks to the PCO that was clicked, so save records provenance
 * and re-detect uses the same cloud.
 */
export const PIPE_TUBE_COLOR = '#22d3ee'; // cyan

export type PipePhase = 'idle' | 'seeking' | 'detecting' | 'review';

export interface PipeToolMode {
  enabled: boolean;
  phase: PipePhase;
  /** The clicked seed point (world / project SRID), set on pick. */
  seed: Vec3 | null;
  sourceLayerId: string | null;
  /** When set, Save updates this existing feature instead of creating one. */
  editFeatureId: string | null;
}

export const DEFAULT_PIPE_TOOL_MODE: PipeToolMode = {
  enabled: false,
  phase: 'idle',
  seed: null,
  sourceLayerId: null,
  editFeatureId: null,
};

export const pipeToolModeAtom = atom<PipeToolMode>(DEFAULT_PIPE_TOOL_MODE);

/**
 * The editable detected pipe. Set by `<PipeTool />` after `detectPipe`
 * resolves; mutated by the panel (radius), the 3D endpoint handles, and chain
 * mode (which appends segments). `lengthM` is derived on save from `vertices`.
 */
export interface PipeDraft {
  /**
   * Centerline polyline (≥2 vertices). A single straight pipe is 2 vertices;
   * chaining appends more. Rendered as one tube per consecutive pair.
   */
  vertices: Vec3[];
  radius: number;
  /**
   * Axis of the most-recently detected segment — used for endpoint-drag
   * direction and the panel's per-segment quality readout.
   */
  axis: Vec3;
  arcCoverageDeg: number;
  rmsM: number;
  inlierCount: number;
  confidence: number;
  /** Whether radius/endpoints were touched after detection (for UX hints). */
  edited: boolean;
}

export const pipeDraftAtom = atom<PipeDraft | null>(null);

export interface PipeDetectProgress {
  phase: PipeDetectPhase;
  done: number;
  total: number;
}

export const pipeDetectProgressAtom = atom<PipeDetectProgress | null>(null);

/**
 * Write-only: enter edit mode on an existing pipe feature. Seeds the tool +
 * draft from the feature's LineString geometry and stored radius/axis so the
 * panel opens pre-populated and the tube preview renders immediately.
 */
export const enterPipeEditModeAtom = atom(
  null,
  (
    _get,
    set,
    payload: {
      featureId: string;
      sourceLayerId: string;
      /** Full centerline polyline from the saved feature's LineString. */
      vertices: Vec3[];
      axis: Vec3;
      radius: number;
      arcCoverageDeg: number;
      rmsM: number;
      inlierCount: number;
      confidence: number;
    },
  ) => {
    if (payload.vertices.length < 2) return;
    // Seed = polyline midpoint, used if the user hits "re-detect".
    const a = payload.vertices[0];
    const b = payload.vertices[payload.vertices.length - 1];
    set(pipeToolModeAtom, {
      enabled: true,
      phase: 'review',
      seed: [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2],
      sourceLayerId: payload.sourceLayerId,
      editFeatureId: payload.featureId,
    });
    set(pipeDraftAtom, {
      vertices: payload.vertices,
      axis: payload.axis,
      radius: payload.radius,
      arcCoverageDeg: payload.arcCoverageDeg,
      rmsM: payload.rmsM,
      inlierCount: payload.inlierCount,
      confidence: payload.confidence,
      edited: false,
    });
  },
);
